import { memo, useMemo } from "react";
import { SingleValue, components, OptionProps, SingleValueProps } from "react-select";
import { ISelectOption, StatusOptionData } from "../../../interfaces";
import DynamicFilter, {
  DynamicFilterField,
  DynamicFilterSelectOption,
} from "../../../components/DynamicFilter";
import AppLogo from "../../AppCountryReports/components/AppLogo";
import { IAdmobApp } from "../interface/index";

interface AppOption extends ISelectOption {
  platform: string;
  appId: string;
  app_logo_url: string;
  app_platform: string;
}


const CustomAppOption = ({ children, ...props }: OptionProps<AppOption>) => {
  const app = props.data;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <div className="min-w-8 w-8 min-h-8 h-8 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <AppLogo
            logoUrl={app?.app_logo_url || null}
            platform={app?.app_platform || ""}
            appName={props.data.label}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{children}</span>
          <span className="text-xs text-gray-500">{props.data.platform as string}</span>
        </div>
      </div>
    </components.Option>
  );
};

const SingleAppValueWithIcon = ({ children, ...props }: SingleValueProps<AppOption>) => {
  const app = props.data as AppOption;
  return (
    <components.SingleValue {...props} key={app?.appId}>
      <div className="flex items-center gap-2">
        <div className="min-w-6 w-6 min-h-6 h-6 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <AppLogo
            logoUrl={app?.app_logo_url || null}
            platform={app?.app_platform || ""}
            appName={props.data.label}
          />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{children}</span>
          <span className="text-xs text-gray-500">{props.data.platform as string}</span>
        </div>
      </div>
    </components.SingleValue>
  );
};

const StatusOption = ({ children, ...props }: OptionProps<StatusOptionData>) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-${data.color}-400`} />
        <span>{children}</span>
      </div>
    </components.Option>
  );
};

const StatusSingleValue = ({ children, ...props }: SingleValueProps<StatusOptionData>) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full bg-${data.color}-400`} />
        <span>{children}</span>
      </div>
    </components.SingleValue>
  );
};

interface AppListFiltersProps {
  t: (key: string) => string;
  openFilter: boolean;
  setOpenFilter: (open: boolean) => void;
  selectedStatus: StatusOptionData | null;
  onStatusChange: (newValue: SingleValue<StatusOptionData>) => void;
  onApplyFilter: () => void;
  // App filter props
  selectedApp: AppOption | null;
  onAppChange: (newValue: SingleValue<AppOption>) => void;
  apps: IAdmobApp[];
  // Platform filter props
  selectedPlatform: ISelectOption | null;
  onPlatformChange: (newValue: SingleValue<ISelectOption>) => void;
  growthIndicatorRange: number[];
}

export const AppListFilters = memo<AppListFiltersProps>(
  ({
    t,
    openFilter,
    setOpenFilter,
    selectedStatus,
    onStatusChange,
    onApplyFilter,
    selectedApp,
    onAppChange,
    apps,
    selectedPlatform,
    onPlatformChange,
    growthIndicatorRange,
  }) => {
    const appOptions: AppOption[] = apps.map((app) => ({
      label: app.app_name,
      value: app.app_slug,
      platform: app.platform,
      appId: app.app_slug,
      app_logo_url: app.logo_url,
      app_platform: app.platform,
    }));

    const platformOptions: ISelectOption[] = [
      { label: t("common.allPlatforms"), value: "all" },
      { label: t("apps.platform.android"), value: "android" },
      { label: t("apps.platform.ios"), value: "ios" },
    ];

    const [minThreshold, maxThreshold] = useMemo(() => growthIndicatorRange, [growthIndicatorRange]);
    const dynamicStatusOptions: StatusOptionData[] = [
      { label: "All Status", value: "all", color: "gray" },
      { label: `Growth (TN/TR > ${maxThreshold})`, value: "success", color: "green" },
      { label: `Stable (${minThreshold} ≤ TN/TR ≤ ${maxThreshold})`, value: "warning", color: "yellow" },
      { label: `Declining (TN/TR < ${minThreshold})`, value: "danger", color: "red" },
    ];

    const fields: DynamicFilterField[] = [
      {
        id: "app",
        type: "select",
        label: t("appCountryWiseReports.filters.apps"),
        placeholder: t("appCountryWiseReports.filters.selectApp"),
        options: appOptions as unknown as DynamicFilterSelectOption[],
        value: selectedApp,
        isClearable: true,
        selectComponents: {
          Option: CustomAppOption,
          SingleValue: SingleAppValueWithIcon,
        },
      },
      {
        id: "status",
        type: "select",
        label: t("common.columns.percentage"),
        placeholder: t("apps.columns.status"),
        options: dynamicStatusOptions as unknown as DynamicFilterSelectOption[],
        value: selectedStatus as unknown as DynamicFilterSelectOption,
        isClearable: true,
        selectComponents: {
          Option: StatusOption,
          SingleValue: StatusSingleValue,
        },
      },
      {
        id: "platform",
        type: "select",
        label: t("common.platform"),
        placeholder: t("common.platform"),
        options: platformOptions as unknown as DynamicFilterSelectOption[],
        value: selectedPlatform as unknown as DynamicFilterSelectOption,
        isClearable: true,
      },
    ];

    return (
      <DynamicFilter
        title={t("common.filter")}
        fields={fields}
        open={openFilter}
        setOpen={setOpenFilter}
        t={t}
        onApply={(values) => {
          const app = (values["app"] as AppOption | null) ?? null;
          const status = (values["status"] as StatusOptionData | null) ?? null;
          const platform = (values["platform"] as ISelectOption | null) ?? null;

          onAppChange(app);
          onStatusChange(status);
          onPlatformChange(platform);

          onApplyFilter();
        }}
        drawerSizeDesktop="1/3"
        drawerDirection="top"
      />
    );
  }
);

export default AppListFilters; 